/**
 * 워크플로 일괄 등록 — 템플릿 워크플로 하나를 골라, 표의 행마다 이름·속성·변수/파라미터 값만 달리한 워크플로를 만든다.
 *
 *   ① 템플릿: 이 프로젝트의 워크플로(서버에 저장된 최신 버전) 또는 워크플로 JSON 파일
 *   ② 표: 열 = 워크플로 항목·변수·노드별 이름/파라미터/속성(골라서 보인다), 행 = 새 워크플로
 *   ③ 검증 → ④ 등록. 표는 CSV 로 내보내고 불러올 수 있다(엑셀에서 채워 넣기).
 *
 * 판단(열 구성·행 적용·검증·CSV)은 bulkCreate.ts 에 있다. 이 화면은 닫았다 열어도 표가 그대로다(usePersistentState).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { AppDispatch, RootState } from '../../store';
import { loadWorkflows, Workflow } from '../../store/project/projectSlice';
import * as repo from '../../services/repo';
import { migrateCanvas } from '../../services/canvasSchema';
import { showAlert, showConfirm } from '../common/AppDialog';
import { compareNames } from '../../utils/workflowFolders';
import { formatNumber } from '../../utils/formatters';
import { usePersistentState } from '../../utils/usePersistentState';
import { downloadBlob, fileNameOf } from '../../services/projectTransfer';
import { logError, logUsage } from '../../services/eventLog';
import {
  applyRow, BulkColumn, BulkIssue, BulkRow, defaultVisibleKeys, newRow, rowsFromCsv, templateColumns, TemplateDoc,
  toCsv, validateRows,
} from './bulkCreate';

type RowResult = { ok: true; id: string } | { ok: false; message: string };

const BulkCreateScreen: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const projectId = useSelector((s: RootState) => s.project.selectedProjectId) || '';
  const workflows = useSelector((s: RootState) => s.project.workflows);
  const usrid = useSelector((s: RootState) => s.auth.user?.usrid || '');
  const viewOnly = useSelector((s: RootState) => s.auth.user?.viewOnly === true);
  const connList = useSelector((s: RootState) => s.connections.list);
  const connections = useMemo(() => (connList || []).map((c: any) => String(c.connection_id)), [connList]);
  const projectWorkflows = useMemo(() => workflows.filter((w) => w.projectId === projectId)
    .sort((a, b) => compareNames(a.folder || '', b.folder || '') || compareNames(a.name, b.name)), [workflows, projectId]);

  // 닫았다 열어도 그대로 (화면 상태 복원 원칙) — 프로젝트가 바뀌면 처음부터
  const [state, setState] = usePersistentState<{
    projectId: string; source: string; template: TemplateDoc | null; visible: string[]; rows: BulkRow[];
    results: Record<number, RowResult>;
  }>('bulkNew:state', { projectId, source: '', template: null, visible: [], rows: [], results: {} });
  const fresh = { projectId, source: '', template: null, visible: [], rows: [], results: {} };
  const st = state.projectId === projectId ? state : fresh;
  /** 앞 상태 위에 고친다 — 한 번에 여러 칸을 바꿔도 서로 덮지 않게 */
  const patch = (p: Partial<typeof st> | ((cur: typeof st) => Partial<typeof st>)) => setState((prev) => {
    const cur = prev.projectId === projectId ? prev : fresh;
    return { ...cur, ...(typeof p === 'function' ? p(cur) : p) };
  });

  const [issues, setIssues] = useState<BulkIssue[] | null>(null);   // null = 아직 검증 안 함(또는 표를 고쳐 다시 해야 함)
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState<{ done: number; total: number } | null>(null);
  const jsonRef = useRef<HTMLInputElement>(null);
  const csvRef = useRef<HTMLInputElement>(null);
  const pickRef = useRef<HTMLDivElement>(null);
  // 열 고르기 창은 바깥을 누르거나 Esc 로 닫는다
  useEffect(() => {
    if (!picking) return undefined;
    const onDown = (e: MouseEvent) => { if (pickRef.current && !pickRef.current.contains(e.target as Node)) setPicking(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setPicking(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
  }, [picking]);

  const cols = useMemo(() => (st.template ? templateColumns(st.template) : []), [st.template]);
  const colByKey = useMemo(() => new Map(cols.map((c) => [c.key, c])), [cols]);
  const shown = useMemo(() => cols.filter((c) => c.key === 'name' || st.visible.includes(c.key)), [cols, st.visible]);

  // ---------------------------------------------------------------- 템플릿
  const applyTemplate = (tpl: TemplateDoc, source: string) => {
    const c = templateColumns(tpl);
    setState({ projectId, source, template: tpl, visible: defaultVisibleKeys(c), rows: [newRow(c, 0)], results: {} });
    setIssues(null);
  };

  const pickWorkflow = async (id: string) => {
    if (!id) return;
    const w = projectWorkflows.find((x) => x.id === id);
    if (!w) return;
    if (st.rows.length && st.template && !(await showConfirm(t('bulk:new.replaceTemplate')))) return;
    try {
      if (!w.curVerNo) { showAlert(t('bulk:new.noSaved', { name: w.name })); return; }
      const doc = await repo.loadCanvas(w.id, w.curVerNo);
      if (!doc) throw new Error(t('bulk:new.readFailed', { name: w.name, ver: w.curVerNo }));
      applyTemplate({ name: w.name, description: w.description, icon: w.icon, folder: w.folder, nodes: doc.nodes, edges: doc.edges, settings: doc.settings },
        `wf:${w.id}`);
    } catch (e) {
      showAlert((e as Error).message);
    }
  };

  const onJson = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const data = JSON.parse(await f.text());
      // 캔버스 내려받기 파일({kind, workflow, canvas}), 서버 캔버스 문서({canvas}), 날것({nodes, edges}) 모두
      const canvas = data?.canvas ?? data;
      if (!Array.isArray(canvas?.nodes) || !Array.isArray(canvas?.edges)) throw new Error(t('bulk:new.badJson'));
      const m = migrateCanvas(canvas);
      if (m.tooNew) throw new Error(t('bulk:new.tooNew', { version: m.docVersion }));
      if (st.rows.length && st.template && !(await showConfirm(t('bulk:new.replaceTemplate')))) return;
      const name = String(data?.workflow?.name || f.name.replace(/\.json$/i, ''));
      applyTemplate({ name, nodes: m.nodes, edges: m.edges, settings: m.settings }, `file:${f.name}`);
    } catch (err) {
      showAlert(err instanceof SyntaxError ? t('bulk:new.badJson') : (err as Error).message);
    }
  };

  // ---------------------------------------------------------------- 표 편집
  const setCell = (i: number, key: string, value: string) => {
    patch((c) => ({ rows: c.rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)) }));
    setIssues(null);
  };
  const addRows = (n: number) => {
    patch((c) => {
      const next = [...c.rows];
      for (let k = 0; k < n; k += 1) next.push(newRow(cols, next.length));
      return { rows: next };
    });
    setIssues(null);
  };
  const copyRow = (i: number) => {
    patch((c) => {
      const copy = { ...c.rows[i], name: `${c.rows[i].name || ''}_copy` };
      return { rows: [...c.rows.slice(0, i + 1), copy, ...c.rows.slice(i + 1)], results: shiftResults(c.results, i + 1, 1) };
    });
    setIssues(null);
  };
  const removeRow = (i: number) => {
    patch((c) => ({ rows: c.rows.filter((_, j) => j !== i), results: shiftResults(c.results, i, -1) }));
    setIssues(null);
  };
  /** 등록을 마친 행을 표에서 뺀다 — 이어서 다음 묶음을 채울 수 있게 (다 빠지면 새 행 하나) */
  const clearDone = () => {
    patch((c) => {
      const keep = c.rows.filter((_, i) => !c.results[i]?.ok);
      const results: Record<number, RowResult> = {};
      c.rows.forEach((_, i) => { if (c.results[i] && !c.results[i].ok) results[keep.indexOf(c.rows[i])] = c.results[i]; });
      return { rows: keep.length ? keep : [newRow(cols, 0)], results };
    });
    setIssues(null);
  };
  const toggleCol = (key: string, on: boolean) => {
    patch((c) => ({ visible: on ? [...c.visible, key] : c.visible.filter((k) => k !== key) }));
  };

  // ---------------------------------------------------------------- CSV
  const exportCsv = () => {
    const keys = shown.map((c) => c.key);
    const blob = new Blob([toCsv(keys, st.rows)], { type: 'text/csv;charset=utf-8' });
    const name = `${fileNameOf(st.template?.name)}_bulk.csv`;
    downloadBlob(blob, name);
    logUsage('canvas.export', { target: st.template?.name, detail: { rows: st.rows.length } });
  };
  const onCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const res = rowsFromCsv(cols, await f.text());
    if (!res.keys.includes('name')) { showAlert(t('bulk:new.csvNoName')); return; }
    if (st.rows.length && !(await showConfirm(t('bulk:new.csvReplace', { n: formatNumber(res.rows.length) })))) return;
    // 파일에 있던 열은 보이게 한다 — 무엇을 바꾸는지 표에서 보여야 한다
    patch({ rows: res.rows, results: {}, visible: Array.from(new Set([...st.visible, ...res.keys])) });
    setIssues(null);
    if (res.unknown.length) showAlert(t('bulk:new.csvUnknown', { cols: res.unknown.join(', ') }));
  };

  // ---------------------------------------------------------------- 검증·등록
  const pendingIdx = st.rows.map((_, i) => i).filter((i) => !st.results[i]?.ok);
  const runValidate = (): BulkIssue[] => {
    // 이미 등록한 행은 빼고, 그 행들이 만든 이름은 "이미 있는 것" 으로 센다
    const existing = [...projectWorkflows.map((w) => ({ name: w.name, folder: w.folder || '' }))];
    const pendingRows = pendingIdx.map((i) => st.rows[i]);
    const found = validateRows(cols, pendingRows, existing, connections).map((x) => ({ ...x, row: pendingIdx[x.row] }));
    setIssues(found);
    return found;
  };
  const errors = (issues || []).filter((x) => x.level === 'error');
  const warnings = (issues || []).filter((x) => x.level === 'warning');
  const issueAt = (row: number, key: string) => (issues || []).find((x) => x.row === row && x.key === key);

  const register = async () => {
    if (!st.template) return;
    const found = issues ?? runValidate();
    if (found.some((x) => x.level === 'error')) { showAlert(t('bulk:new.fixErrors', { n: formatNumber(found.filter((x) => x.level === 'error').length) })); return; }
    const todo = pendingIdx;
    if (!todo.length) { showAlert(t('bulk:new.nothing')); return; }
    const warnText = found.some((x) => x.level === 'warning') ? t('bulk:new.withWarnings', { n: formatNumber(found.filter((x) => x.level === 'warning').length) }) : '';
    if (!(await showConfirm(t('bulk:new.registerConfirm', { n: formatNumber(todo.length) }) + warnText, { okText: t('bulk:new.register') }))) return;

    const results = { ...st.results };
    setBusy({ done: 0, total: todo.length });
    for (let k = 0; k < todo.length; k += 1) {
      const i = todo[k];
      const w = applyRow(st.template, cols, st.rows[i]);
      try {
        const created: Workflow = await repo.createWorkflow({ projectId, name: w.name, description: w.description, icon: w.icon, folder: w.folder });
        await repo.saveCanvas(created.id, 0, { nodes: w.nodes, edges: w.edges, settings: w.settings }, usrid);
        results[i] = { ok: true, id: created.id };
      } catch (e) {
        results[i] = { ok: false, message: (e as Error)?.message || String(e) };
        logError('workflow.bulkCreate', e as Error, { target: w.name });
      }
      setBusy({ done: k + 1, total: todo.length });
    }
    setBusy(null);
    patch({ results });
    setIssues(null);
    await dispatch(loadWorkflows(projectId));
    const ok = todo.filter((i) => results[i]?.ok).length;
    logUsage('workflow.bulkCreate', { target: st.template.name, detail: { ok, failed: todo.length - ok } });
    showAlert(ok === todo.length
      ? t('bulk:new.registered', { n: formatNumber(ok) })
      : t('bulk:new.registeredPartly', { n: formatNumber(ok), failed: formatNumber(todo.length - ok) }));
  };

  const issueText = (x: BulkIssue): string => String(t(`bulk:new.issue.${x.code}`, (x.params || {}) as Record<string, unknown>));
  const colTitle = (c: BulkColumn) => (c.kind === 'wf' ? t(`bulk:new.wf.${c.field}`) : c.kind === 'var' ? c.key
    : c.kind === 'label' ? t('bulk:new.nodeLabel') : c.kind === 'param' ? t('bulk:new.param', { name: c.field }) : c.field);

  // 표 머리 첫 줄 — 같은 노드의 열을 묶는다
  const groups: Array<{ title: string; span: number }> = [];
  shown.forEach((c) => {
    const g = c.kind === 'wf' ? t('bulk:new.groupWorkflow') : c.kind === 'var' ? t('bulk:new.groupVars') : c.nodeName || '';
    const last = groups[groups.length - 1];
    const same = last && last.title === g && (c.kind === 'wf' || c.kind === 'var' || shown[shown.indexOf(c) - 1]?.nodeId === c.nodeId);
    if (same) last.span += 1; else groups.push({ title: g, span: 1 });
  });

  // 열 고르기 목록 — 워크플로 / 변수 / 노드별
  const pickGroups = useMemo(() => {
    const out: Array<{ title: string; cols: BulkColumn[] }> = [];
    cols.forEach((c) => {
      const title = c.kind === 'wf' ? t('bulk:new.groupWorkflow') : c.kind === 'var' ? t('bulk:new.groupVars') : `${c.nodeName} · ${c.nodeType}`;
      const id = c.kind === 'wf' || c.kind === 'var' ? c.kind : c.nodeId;
      const last = out[out.length - 1] as any;
      if (last && last.id === id) last.cols.push(c); else out.push({ title, cols: [c], id } as any);
    });
    return out;
  }, [cols, t]);

  const inp = 'w-full min-w-[7rem] px-1.5 py-1 text-xs border rounded bg-white';
  return (
    <div className="flex flex-col h-full w-full bg-gray-50 p-4 gap-3 overflow-hidden">
      <div className="text-xs text-gray-500 shrink-0">{t('bulk:new.intro')}</div>

      {/* ① 템플릿 */}
      <div className="bg-white rounded-lg shadow-sm border p-3 shrink-0 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-gray-700">{t('bulk:new.template')}</span>
        <select className="px-2 py-1 text-xs border rounded bg-white min-w-[16rem]" value={st.source.startsWith('wf:') ? st.source.slice(3) : ''}
          onChange={(e) => pickWorkflow(e.target.value)}>
          <option value="">{t('bulk:new.pickWorkflow')}</option>
          {projectWorkflows.map((w) => (
            <option key={w.id} value={w.id} disabled={!w.curVerNo}>{w.folder ? `${w.folder} / ` : ''}{w.name}{w.curVerNo ? '' : ` ${t('bulk:new.unsavedMark')}`}</option>
          ))}
        </select>
        <span className="text-gray-400">{t('bulk:new.or')}</span>
        <button type="button" className="px-2.5 py-1 border rounded hover:bg-gray-100" onClick={() => jsonRef.current?.click()}>{t('bulk:new.loadJson')}</button>
        <input ref={jsonRef} type="file" accept=".json,application/json" className="hidden" onChange={onJson} />
        {st.template && (
          <span className="ml-2 text-gray-600">
            {st.source.startsWith('file:') ? `📄 ${st.source.slice(5)} · ` : ''}
            {t('bulk:new.templateSummary', { nodes: formatNumber(st.template.nodes.length), edges: formatNumber(st.template.edges.length) })}
          </span>
        )}
      </div>

      {!st.template ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">{t('bulk:new.pickFirst')}</div>
      ) : (
        <>
          {/* ② 도구 줄 */}
          <div className="flex flex-wrap items-center gap-2 text-xs shrink-0">
            <div className="relative" ref={pickRef}>
              <button type="button" className={classNames('px-2.5 py-1 border rounded bg-white hover:bg-gray-100', picking && 'border-blue-500')} onClick={() => setPicking((v) => !v)} aria-expanded={picking}>
                {t('bulk:new.columns', { n: formatNumber(shown.length), total: formatNumber(cols.length) })}
              </button>
              {picking && (
                <div className="absolute left-0 top-full mt-1 z-30 w-[26rem] max-h-[60vh] overflow-y-auto bg-white border rounded-lg shadow-lg p-2">
                  {pickGroups.map((g, gi) => (
                    <div key={gi} className="mb-2">
                      <div className="flex items-center gap-2 font-semibold text-gray-700 mb-0.5">
                        <span className="truncate">{g.title}</span>
                        <button type="button" className="ml-auto text-[0.6875rem] text-blue-600 hover:underline"
                          onClick={() => patch({ visible: Array.from(new Set([...st.visible, ...g.cols.map((c) => c.key)])) })}>{t('bulk:new.all')}</button>
                        <button type="button" className="text-[0.6875rem] text-blue-600 hover:underline"
                          onClick={() => patch({ visible: st.visible.filter((k) => !g.cols.some((c) => c.key === k)) })}>{t('bulk:new.none')}</button>
                      </div>
                      <div className="grid grid-cols-2 gap-x-3">
                        {g.cols.map((c) => (
                          <label key={c.key} className="flex items-center gap-1.5 py-0.5 text-gray-700 min-w-0" title={`${c.key} = ${c.templateValue}`}>
                            <input type="checkbox" disabled={c.key === 'name'} checked={c.key === 'name' || st.visible.includes(c.key)} onChange={(e) => toggleCol(c.key, e.target.checked)} />
                            <span className="truncate">{colTitle(c)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button type="button" className="px-2.5 py-1 border rounded bg-white hover:bg-gray-100" onClick={() => addRows(1)}>{t('bulk:new.addRow')}</button>
            <button type="button" className="px-2.5 py-1 border rounded bg-white hover:bg-gray-100" onClick={() => addRows(10)}>{t('bulk:new.addRows', { n: 10 })}</button>
            <span className="mx-1 h-4 border-l" />
            <button type="button" className="px-2.5 py-1 border rounded bg-white hover:bg-gray-100" onClick={exportCsv} title={t('bulk:new.csvExportTitle')}>{t('bulk:new.csvExport')}</button>
            <button type="button" className="px-2.5 py-1 border rounded bg-white hover:bg-gray-100" onClick={() => csvRef.current?.click()} title={t('bulk:new.csvImportTitle')}>{t('bulk:new.csvImport')}</button>
            <input ref={csvRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onCsv} />
            {Object.values(st.results).some((r) => r.ok) && (
              <button type="button" className="px-2.5 py-1 border rounded bg-white hover:bg-gray-100" onClick={clearDone} title={t('bulk:new.clearDoneTitle')}>{t('bulk:new.clearDone')}</button>
            )}
            <span className="ml-auto text-gray-500">{t('bulk:new.rowCount', { n: formatNumber(st.rows.length), pending: formatNumber(pendingIdx.length) })}</span>
            <button type="button" className="px-3 py-1 border rounded bg-white hover:bg-gray-100" onClick={runValidate} disabled={!!busy}>{t('bulk:new.validate')}</button>
            <button type="button" className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={register} disabled={viewOnly || !!busy || !pendingIdx.length} title={viewOnly ? t('bulk:viewOnly') : t('bulk:new.registerTitle')}>
              {busy ? t('bulk:new.registering', { done: formatNumber(busy.done), total: formatNumber(busy.total) }) : t('bulk:new.register')}
            </button>
          </div>

          {/* ③ 표 */}
          <div className="flex-1 min-h-0 overflow-auto bg-white border rounded-lg">
            <table className="text-xs border-collapse">
              <thead className="sticky top-0 z-10 bg-gray-50">
                <tr>
                  <th className="px-2 py-1 border-b border-r text-gray-400" rowSpan={2}>#</th>
                  {groups.map((g, i) => <th key={i} colSpan={g.span} className="px-2 py-1 border-b border-r text-left font-semibold text-gray-700 whitespace-nowrap">{g.title}</th>)}
                  <th className="px-2 py-1 border-b text-gray-500" rowSpan={2}>{t('bulk:new.status')}</th>
                  <th className="px-2 py-1 border-b" rowSpan={2} />
                </tr>
                <tr>
                  {shown.map((c) => (
                    <th key={c.key} className="px-2 py-1 border-b border-r text-left font-medium text-gray-500 whitespace-nowrap" title={c.key}>
                      {colTitle(c)}{c.valueType !== 'string' && <span className="ml-1 text-gray-400">({c.valueType === 'number' ? '#' : 'Y/N'})</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {st.rows.map((r, i) => {
                  const res = st.results[i];
                  const locked = !!res?.ok;
                  return (
                    <tr key={i} className={classNames('border-b border-gray-100', locked && 'bg-emerald-50/50')}>
                      <td className="px-2 py-1 border-r text-gray-400 tabular-nums text-right">{i + 1}</td>
                      {shown.map((c) => {
                        const is = issueAt(i, c.key);
                        return (
                          <td key={c.key} className="px-1 py-0.5 border-r" title={is ? issueText(is) : undefined}>
                            <input className={classNames(inp, is?.level === 'error' ? 'border-red-600' : is?.level === 'warning' ? 'border-amber-500' : 'border-gray-200',
                              r[c.key] !== c.templateValue && c.key !== 'name' && 'text-blue-700')}
                              value={r[c.key] ?? ''} disabled={locked} placeholder={c.kind === 'var' ? t('bulk:new.varPlaceholder') : ''}
                              onChange={(e) => setCell(i, c.key, e.target.value)} />
                          </td>
                        );
                      })}
                      <td className="px-2 py-1 whitespace-nowrap">
                        {res?.ok && <span className="text-emerald-700">{t('bulk:new.done')}</span>}
                        {res && !res.ok && <span className="text-red-600" title={res.message}>{t('bulk:new.failed')}</span>}
                      </td>
                      <td className="px-1 py-1 whitespace-nowrap">
                        <button type="button" className="px-1.5 text-gray-500 hover:text-blue-700" onClick={() => copyRow(i)} title={t('bulk:new.copyRow')}>⧉</button>
                        <button type="button" className="px-1.5 text-gray-500 hover:text-red-600 disabled:opacity-30" onClick={() => removeRow(i)} disabled={locked} title={t('bulk:new.removeRow')}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ④ 검증 결과 */}
          <div className="shrink-0 max-h-32 overflow-y-auto text-xs">
            {issues === null ? (
              <span className="text-gray-400">{t('bulk:new.notValidated')}</span>
            ) : !issues.length ? (
              <span className="text-emerald-700">{t('bulk:new.valid', { n: formatNumber(pendingIdx.length) })}</span>
            ) : (
              <>
                <div className="mb-1 font-semibold">
                  <span className="text-red-600">{t('bulk:new.errorCount', { n: formatNumber(errors.length) })}</span>
                  <span className="ml-3 text-amber-600">{t('bulk:new.warningCount', { n: formatNumber(warnings.length) })}</span>
                </div>
                <ul>
                  {[...errors, ...warnings].map((x, k) => (
                    <li key={k} className={x.level === 'error' ? 'text-red-600' : 'text-amber-600'}>
                      {t('bulk:new.issueLine', { row: x.row + 1, col: colByKey.get(x.key) ? colTitle(colByKey.get(x.key) as BulkColumn) : x.key })} {issueText(x)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/** 행을 끼우거나 뺄 때 등록 결과의 행 번호를 함께 민다 */
function shiftResults(results: Record<number, RowResult>, from: number, by: number): Record<number, RowResult> {
  const out: Record<number, RowResult> = {};
  Object.entries(results).forEach(([k, v]) => {
    const i = Number(k);
    if (by < 0 && i === from) return;
    out[i >= from ? i + by : i] = v;
  });
  return out;
}

export default BulkCreateScreen;
